import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { ChevronRightIcon, PlusIcon, CheckCircleIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const Support: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const { user } = useAuth(); // Kullanılmıyor
  const { tickets, isLoading } = useData();
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved'>('all');

  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter === 'all') return true;
    return ticket.status === statusFilter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />;
      case 'in_progress':
        return <ClockIcon className="w-5 h-5 text-amber-400" />;
      case 'resolved':
        return <CheckCircleIcon className="w-5 h-5 text-green-400" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'in_progress':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'resolved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'high':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'normal':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'low':
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return t('common.days', { count: 1 });
    } else if (diffDays < 7) {
      return t('common.days', { count: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('support.title')}
        </h1>
        <p className="text-gray-300">
          {t('support.subtitle')}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/support/new')}
            className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('support.newTicket')}</span>
          </button>
        </div>

        {/* Status Filter */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-300">
            {t('support.status')}:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="bg-gray-800/50 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm"
          >
            <option value="all">{t('common.all')}</option>
            <option value="open">{t('support.statuses.open')}</option>
            <option value="in_progress">{t('support.statuses.in_progress')}</option>
            <option value="resolved">{t('support.statuses.resolved')}</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
            <ExclamationTriangleIcon className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-medium text-white mb-2">
            {t('support.noTickets')}
          </h3>
          <p className="text-gray-400 mb-6">
            {t('support.noTicketsDescription')}
          </p>
          <button
            onClick={() => navigate('/support/new')}
            className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white px-6 py-3 rounded-lg inline-flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('support.createFirstTicket')}</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => navigate(`/support/${ticket.id}`)}
              className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300 cursor-pointer p-6 hover:bg-gray-800/50 transform hover:scale-[1.02] shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <h3 className="text-lg font-semibold text-white">
                      {ticket.subject}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                      {t(`support.statuses.${ticket.status}`)}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                      {t(`support.priorities.${ticket.priority}`)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-400">
                    <span className="flex items-center space-x-1">
                      <span>{t('support.category')}:</span>
                      <span className="font-medium text-gray-300">{t(`support.categories.${ticket.category}`)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span>{t('support.created')}:</span>
                      <span className="font-medium text-gray-300">{formatDate(ticket.created_at)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <span>{t('support.lastUpdate')}:</span>
                      <span className="font-medium text-gray-300">{formatDate(ticket.updated_at)}</span>
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {getStatusIcon(ticket.status)}
                  <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Support; 